import React from 'react'
import {
  Box,
  FormHelperText,
  TextField,
  styled,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { Styles } from '../../../../types'
import { tokens } from '../../../../styles/materialTheme'
import useEmailValidation from '../../../../utils/hooks/useEmailValidation'

const styles: Styles = {
  textContainer: (theme) => ({
    '.MuiOutlinedInput-root': {
      width: '100%',
      maxWidth: '100%',
    },
    width: 'calc(100% - 4rem)',
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
    },
  }),
  fieldContainer: (theme) => ({
    width: '50%',
    paddingTop: 0,
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
      paddingLeft: 0,
    },
    [theme.breakpoints.only('tablet')]: {
      paddingLeft: 0,
    },
  }),
  requiredError: {
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '0.875rem',
    color: tokens.colors.rsmRed.secondary,
  },
}

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

interface EmailAddressProps {
  handleChange: (email: string) => void
  label?: string
  placeholder?: string
  errorValidationRules?: (value: string) => string | true
}

const EmailAddress = ({
  handleChange,
  label = 'Invoicing.EmailReceipents',
  placeholder = 'Invoicing.EnterEmail',
  errorValidationRules,
}: EmailAddressProps) => {
  const { control } = useFormContext()
  const { t } = useTranslation()
  const resolvedValidationRule: (value: string) => true | string =
    errorValidationRules || ((value: string) => useEmailValidation(value, t))

  return (
    <Box sx={styles.fieldContainer}>
      <StyledLabel htmlFor="emailAddress">{t(label)}</StyledLabel>
      <Controller
        name="emailAddress"
        control={control}
        defaultValue=""
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <Box sx={styles.textContainer}>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                id="emailAddress"
                inputRef={ref}
                type="text"
                placeholder={t(placeholder)}
                value={value}
                error={!!error}
                data-testid="Inp_PaymentType_EmailAddress"
                onChange={(e) => {
                  onChange(e)
                  handleChange(e.target.value)
                }}
                inputProps={{
                  'aria-describedby': 'emailAddressErrorText',
                  style: { paddingRight: value ? 32 : undefined },
                }}
              />
              {value && (
                <IconButton
                  aria-label="clear email"
                  onClick={() => {
                    onChange({ target: { value: '' } })
                    handleChange('')
                  }}
                  size="small"
                  disableRipple
                  disableFocusRipple
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    '&:hover': {
                      background: 'none',
                    },
                  }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            {error && (
              <FormHelperText
                id="emailAddressErrorText"
                data-testid="Error_PaymentType_EmailAddress"
                sx={styles.requiredError}>
                {error.message}
              </FormHelperText>
            )}
          </Box>
        )}
        rules={{
          validate: (value) => resolvedValidationRule(value),
        }}
      />
    </Box>
  )
}

export default EmailAddress
